import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, ReplaySubject, of, catchError, take, throwError, EMPTY } from 'rxjs';
import { mergeMap, finalize, shareReplay } from 'rxjs/operators';
import { Navigation } from 'app/core/navigation/navigation.types';
import { FuseNavigationItem } from '@fuse/components/navigation';
import { environment } from '../../../environments/environment';
import { apiResources } from 'app/services/api/api.resources';
import { TabPermissionService } from 'app/core/auth/tab-permission.service';

@Injectable({
    providedIn: 'root'
})
export class NavigationService
{
    private _navigation: ReplaySubject<Navigation> = new ReplaySubject<Navigation>(1);
    private _loaded = false;
    /** Observable compartilhado do request em voo (dedup de get() — evita N
     *  chamadores no mesmo tick, ex.: RolesAccessGuard canMatch ×7, dispararem
     *  N requests de GET /navigation/menu). */
    private _pending: Observable<Navigation> | null = null;
    /** Época do cache: incrementada em reset() — resposta HTTP em voo de uma
     *  época anterior é DESCARTADA ao completar (nunca repopula o menu com
     *  dados do usuário/sessão anterior — race in-flight × reset). */
    private _epoch = 0;

    constructor(
        private _httpClient: HttpClient,
        private _tabPermissionService: TabPermissionService
    )
    {
    }

    get navigation$(): Observable<Navigation>
    {
        return this._navigation.asObservable();
    }

    get(): Observable<Navigation>
    {
        if (this._loaded)
        {
            return this._navigation.asObservable().pipe(take(1));
        }

        // Dedup: request em voo compartilhado entre chamadores do mesmo tick
        // (shareReplay: 1 execução da fonte, N receivers).
        if (this._pending)
        {
            return this._pending;
        }

        const url = environment.config.apiUrl + apiResources.v1 + apiResources.navigation.base + apiResources.navigation.menu;

        const epoch = this._epoch;
        const pending = this._httpClient.get<any>(url).pipe(
            mergeMap((response) =>
            {
                // Epoch guard: resposta de época anterior (reset() durante o voo)
                // é DESCARTADA — nunca repopula _loaded/_navigation com dados do
                // usuário/sessão anterior.
                if (epoch !== this._epoch)
                {
                    return EMPTY;
                }

                const menuItems = response.menu || response.data || [];
                const permissionsTree = response.permissionsTree;
                if (permissionsTree && typeof permissionsTree === 'object')
                {
                    this._tabPermissionService.setPermissionsFromTree(permissionsTree);
                }
                const filtered = this._mapApiMenuToNavigation(menuItems);
                const navigation: Navigation = { default: filtered };
                this._navigation.next(navigation);
                this._loaded = true;
                return of(navigation);
            }),
            catchError((error) => {
                if (error?.status === 401)
                {
                    return throwError(() => error);
                }
                // Erro não-401: entrega o fallback (menu vazio) PARA ESTA chamada,
                // mas NÃO marca _loaded — a próxima navegação tenta buscar o menu
                // de novo (evita lockout silencioso com menu vazio cacheado por
                // toda a sessão quando o backend falha de forma transitória).
                const fallback = this._buildFallbackNavigation();
                this._navigation.next(fallback);
                return of(fallback);
            }),
            finalize(() =>
            {
                // Só limpa o dedup se a chain que completou é a corrente — evita
                // que uma chain antiga (epoch anterior) zere o _pending da nova.
                if (this._pending === pending)
                {
                    this._pending = null;
                }
            }),
            shareReplay({ bufferSize: 1, refCount: false })
        );
        this._pending = pending;

        return pending;
    }

    reset(): void
    {
        this._epoch++;
        this._loaded = false;
        this._navigation = new ReplaySubject<Navigation>(1);
    }

    /**
     * PONTO DE MONTAGEM do side menu (Regra 1 — sidebar/navegação/abas):
     * "módulo só aparece na sidebar se toggle habilitado E ≥1 aba habilitada".
     *
     * A derivação "módulo full com TODAS as tabs hidden → state=hidden" é feita
     * no BACKEND (GetNavigationMenuAction) — o cliente não re-deriva abas aqui.
     * Do lado do cliente, este é o ÚNICO ponto que monta a navegação renderizada
     * (layouts classic/dense consomem navigation$.default):
     *  - itens/children com state==='hidden' são filtrados (nunca viram <li>);
     *  - grupos (type: 'group') SEM children visíveis são pulados — um container
     *    vazio nunca é renderizado na sidebar (defesa da Regra 1).
     * Se um módulo com link aparecer aqui mas o usuário não tiver nenhuma aba,
     * o FirstAccessibleTabGuard (roteamento do módulo) expulsa para /home —
     * nunca aterrissa em aba oculta.
     */
    private _mapApiMenuToNavigation(items: any[]): FuseNavigationItem[]
    {
        const result: FuseNavigationItem[] = [];

        for (const item of items)
        {
            if (item.state === 'hidden')
            {
                continue;
            }

            if (item.type === 'group')
            {
                const visibleChildren = (item.children || [])
                    .filter((child: any) => child.state !== 'hidden')
                    .map((child: any) => this._mapItem(child));

                if (visibleChildren.length === 0)
                {
                    continue;
                }

                result.push({
                    id: String(item.id),
                    title: item.title,
                    type: 'group',
                    children: visibleChildren,
                });
            }
            else
            {
                result.push(this._mapItem(item));
            }
        }

        this._applyExactMatchForPrefixLinks(result);

        return result;
    }

    private _mapItem(item: any): FuseNavigationItem
    {
        const mapped: FuseNavigationItem = {
            id: String(item.id),
            title: item.title,
            type: item.type || 'basic',
        };

        if (item.icon)
        {
            mapped.icon = item.icon;
        }

        if (item.link)
        {
            mapped.link = item.link;
        }

        if (item.admin_only)
        {
            mapped.meta = { adminOnly: true };
        }

        if (item.children && item.children.length > 0)
        {
            const visibleChildren = item.children
                .filter((child: any) => child.state !== 'hidden')
                .map((child: any) => this._mapItem(child));

            if (visibleChildren.length > 0)
            {
                mapped.children = visibleChildren;
            }
        }

        return mapped;
    }

    private _buildFallbackNavigation(): Navigation
    {
        return { default: [] };
    }

    private _applyExactMatchForPrefixLinks(items: FuseNavigationItem[]): void
    {
        const allLinks = this._collectAllLinks(items);

        this._setExactMatch(items, allLinks);
    }

    private _collectAllLinks(items: FuseNavigationItem[]): string[]
    {
        const links: string[] = [];

        for (const item of items)
        {
            if (item.link)
            {
                links.push(item.link);
            }

            if (item.children)
            {
                links.push(...this._collectAllLinks(item.children));
            }
        }

        return links;
    }

    private _setExactMatch(items: FuseNavigationItem[], allLinks: string[]): void
    {
        for (const item of items)
        {
            if (item.link)
            {
                const isPrefixOfAnother = allLinks.some(other =>
                    other !== item.link && other.startsWith(item.link + '/')
                );

                if (isPrefixOfAnother)
                {
                    item.exactMatch = true;
                }
            }

            if (item.children)
            {
                this._setExactMatch(item.children, allLinks);
            }
        }
    }
}
